import { getPlatformBrand } from '@/utils/platform';
import { formatCompact } from '@/utils/format';
import type { CreatorPlatformAccount } from '@/types/portal';

/** Followers split by platform, in each platform's colours, growing in from the left. */
export function ReachBar({ platforms, className = '' }: { platforms: CreatorPlatformAccount[]; className?: string }) {
  const total = platforms.reduce((sum, p) => sum + p.followers, 0);
  if (total === 0) return null;
  return (
    <div className={`flex h-1.5 overflow-hidden rounded-full bg-background-200 ${className}`} aria-hidden="true">
      {platforms.map((account, index) => {
        const brand = getPlatformBrand(account.platform);
        return (
          <span
            key={`${account.platform}-${account.handle}`}
            title={`${account.platform}: ${formatCompact(account.followers)}`}
            className={`share-grow h-full ${index > 0 ? 'border-l border-background-50' : ''}`}
            style={{
              width: `${(account.followers / total) * 100}%`,
              background: brand?.gradient ?? brand?.color ?? 'oklch(var(--foreground-400))',
              animationDelay: `${index * 120}ms`,
            }}
          />
        );
      })}
    </div>
  );
}

/** Engagement on a 0–10% scale, with the usual 1–3% band shaded behind it. */
export function EngagementMeter({ rate }: { rate: number }) {
  const position = Math.min(100, (rate / 10) * 100);
  return (
    <div className="relative h-1.5 rounded-full bg-background-200" aria-hidden="true">
      {/* The usual range, 1–3%. */}
      <span className="absolute inset-y-0 rounded-full bg-background-300" style={{ left: '10%', width: '20%' }} />
      <span
        className="share-grow absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-secondary-400 to-accent-500"
        style={{ width: `${position}%` }}
      />
    </div>
  );
}

/** The fit score (0–100) as a ring that draws itself in; a dash when there's no brief to score against. */
export function FitRing({ score, size = 48 }: { score: number | null; size?: number }) {
  const radius = 19;
  const circumference = 2 * Math.PI * radius;
  const share = score === null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const tone =
    score === null
      ? 'stroke-background-300'
      : score >= 75
        ? 'stroke-accent-500'
        : score >= 50
          ? 'stroke-secondary-400'
          : 'stroke-primary-400';
  return (
    <span
      className="relative flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      title={score === null ? 'Describe your brief to get a fit score' : `Fit score ${score} out of 100`}
    >
      <svg viewBox="0 0 46 46" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="23" cy="23" r={radius} fill="none" strokeWidth="4" className="stroke-background-200" />
        <circle
          cx="23"
          cy="23"
          r={radius}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          className={`ring-draw ${tone}`}
          style={{ strokeDasharray: circumference, strokeDashoffset: circumference * (1 - share) }}
        />
      </svg>
      <span className="absolute flex flex-col items-center leading-none">
        <span className="font-heading text-sm font-bold text-foreground-950">{score ?? '—'}</span>
        <span className="text-[8px] font-label uppercase tracking-wide text-foreground-500">fit</span>
      </span>
    </span>
  );
}
